import {
  BeneficiaryDimensionsDTO,
  BeneficiaryFiltersDTO,
  BeneficiaryInsightDisplay,
  BeneficiaryInsightMetricsDTO,
} from './beneficiary'
import { TimeType } from './generic'
import { TaskDTO } from './task'

type AssistantItemType =
  | 'chart'
  | 'cta'
  | 'message'
  | 'processingMessage'
  | 'summary'
  | 'task'
  | 'tip'
  | 'insightDisplay'

export interface Item<T extends AssistantItemType> {
  generationId: string
  id: string
  sentAt: TimeType
  type: T
}

export interface AssistantItems {
  chart: ChartItem // LEGACY
  insightDisplay: InsightDisplayItem
  beneficiaryInsights: BeneficiaryInsightsMessage
  beneficiaryInsightsCta: BeneficiaryInsightsCta
  message: Message
  processingMessage: ProcessingMessage
  summary: Summary
  task: Task
  tip: Tip
  beneficiaryProfile: CtaProfile
}

export type AssistantItem = AssistantItems[keyof AssistantItems]

export type Traceable = { traceUrl?: string }

type BeneficiaryInsightsCta = Item<'cta'> & {
  cta: 'beneficiaryInsights'
  filters?: BeneficiaryFiltersDTO // Filters used to generate the insights or the list of beneficiaries
  metrics?: BeneficiaryInsightMetricsDTO
  dimensions?: BeneficiaryDimensionsDTO
  recommendedDisplay?: BeneficiaryInsightDisplay
  explanations?: string // Explanations of the insights in Markdown format
  csv?: string // Metrics in the CSV format
} & Traceable

type BeneficiaryInsightsMessage = Item<'message'> & {
  content: string
  explanations: string
  isFromUser: false
} & Traceable

export type BeneficiaryInsightsAssistemItems = (
  | AssistantItems['beneficiaryInsights']
  | AssistantItems['insightDisplay']
  | AssistantItems['beneficiaryInsightsCta']
)[]

type CtaProfile = Item<'cta'> & {
  profileId: string
  content: string
  cta: 'profile'
} & Traceable

export type Chart = {
  type: 'pie' | 'bar' | 'line'
  payload: {
    labels: string[]
    datasets: {
      label: string
      data: number[]
      backgroundColor?: string[] | string
      borderColor?: string[] | string
      borderWidth?: number
    }[]
  }
}

export type Kpi = {
  type: 'kpi'
  payload: {
    title: string
    description: string
    value: number
  }
}

export type InsightBaseDisplay = {
  title: string
  description: string
}

export type InsightKpiDisplay = InsightBaseDisplay & {
  type: 'kpi'
  value: number
}

export type InsightChartDisplay = InsightBaseDisplay & {
  type: 'chart'
  value: Chart
}

export type InsightDisplay = InsightKpiDisplay | InsightChartDisplay

type InsightDisplayItem = Item<'insightDisplay'> & { data: InsightDisplay } & Traceable

type ChartItem = Item<'chart'> & {
  data: Chart
} & Traceable

type Tip = Item<'tip'> & {
  title: string
  content: string
} & Traceable

type Task = Item<'task'> &
  TaskDTO & {
    isBeneficiaryTask: boolean
  } & Traceable

type Summary = Item<'summary'> & {
  content: string
} & Traceable

type Message = Item<'message'> & {
  isFromUser?: true
  data?: object
} & ({ content: string } | { transKeyContent: string }) &
  Traceable

type ProcessingMessage = Item<'processingMessage'> &
  (
    | {
        contentWhileProcessing: string
        contentAfterProcessing: string
      }
    | {
        transKeyWhileProcessing: string
        transKeyAfterProcessing: string
      }
  )

export namespace TraceUrlHelper {
  const cached = 'cached=true'
  const db = 'db=true'

  export function isTraceUrlCached(traceUrl: string | undefined): boolean {
    return !!traceUrl?.endsWith(cached)
  }

  export function isTraceUrlInDb(traceUrl: string | undefined): boolean {
    return !!traceUrl?.endsWith(db)
  }

  export function markTraceUrlAsCached(traceUrl: string): string {
    return removeMark(traceUrl) + `?${cached}`
  }

  export function markTraceUrlAsInDb(traceUrl: string): string {
    return removeMark(traceUrl) + `?${db}`
  }

  export function removeMark(traceUrl: string): string {
    return traceUrl ? traceUrl.split('?')[0] || '' : ''
  }
}
